import { useMemo } from "react";
import { t } from "@/shared/lib/th-translations";
import type {
  PerformanceMetric,
  PerformanceProcessBucket,
} from "@/shared/types/production";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  aggregateEmployees,
  computeProcessStats,
  formatMetricDisplay,
  metricDisplayLabel,
  percentileToColor,
} from "@/frontend/lib/production-stats";

interface Props {
  processes: PerformanceProcessBucket[];
  metric: PerformanceMetric;
}

export default function PerformanceRankedList({ processes, metric }: Props) {
  const aggregates = useMemo(
    () => aggregateEmployees(processes, metric),
    [processes, metric],
  );
  const processStats = useMemo(
    () => processes.map((p) => computeProcessStats(p, metric)),
    [processes, metric],
  );
  const metricLabel = metricDisplayLabel(metric);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t("production.performance.viz.rankedList.title")}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {t("production.performance.viz.rankedList.legend")}
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {aggregates.map((emp, idx) => (
            <div
              key={emp.employeeId}
              className="grid grid-cols-[auto_180px_1fr_auto] gap-3 items-center py-2 border-b last:border-0"
            >
              {/* Rank number */}
              <div className="w-8 text-center text-sm font-mono text-muted-foreground">
                #{idx + 1}
              </div>

              {/* Employee name + aggregate chip */}
              <div className="flex items-center gap-2 min-w-0">
                <span className="truncate font-medium" title={emp.employeeName}>
                  {emp.employeeName}
                </span>
                <span
                  className={`shrink-0 px-1.5 py-0.5 rounded text-xs font-medium ${percentileToColor(emp.avgPercentile)}`}
                >
                  {Math.round(emp.avgPercentile)}%
                </span>
              </div>

              {/* Per-process percentile mini bars */}
              <div className="flex flex-wrap gap-1">
                {processStats.map((stats) => {
                  const pct = stats.percentileByEmployee.get(emp.employeeId);
                  const val = stats.valueByEmployee.get(emp.employeeId);
                  if (pct === undefined || val === undefined) {
                    return (
                      <div
                        key={stats.processTitle}
                        className="flex flex-col items-start w-20"
                        title={`${stats.processTitle}\n${t("production.performance.viz.heatmap.noData")}`}
                      >
                        <span className="text-[10px] text-muted-foreground truncate w-full">
                          {stats.processTitle}
                        </span>
                        <div className="h-2 bg-gray-100 rounded w-full" />
                      </div>
                    );
                  }
                  const bgColor =
                    pct < 20
                      ? "bg-red-500"
                      : pct < 40
                        ? "bg-red-300"
                        : pct < 60
                          ? "bg-yellow-400"
                          : pct < 80
                            ? "bg-green-300"
                            : "bg-green-500";
                  return (
                    <div
                      key={stats.processTitle}
                      className="flex flex-col items-start w-20"
                      title={`${stats.processTitle}: ${metricLabel}: ${formatMetricDisplay(
                        val,
                        metric,
                      )} (อันดับเปรียบเทียบ ${Math.round(pct)}%)`}
                    >
                      <span
                        className="text-[10px] text-muted-foreground truncate w-full"
                        title={stats.processTitle}
                      >
                        {stats.processTitle}
                      </span>
                      <div className="h-2 bg-gray-100 rounded w-full overflow-hidden">
                        <div
                          className={`h-full ${bgColor} transition-all`}
                          style={{ width: `${Math.max(4, pct)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total value */}
              <div className="text-right text-sm font-mono text-muted-foreground whitespace-nowrap">
                {metricLabel}: {formatMetricDisplay(emp.totalValue, metric)}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
